/**
 * NotificationBell Component
 * Bell icon with unread count badge for notification indicators
 */

import { cn } from '@commander/ui';
import { RiNotification3Line } from '@remixicon/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useUnreadNotificationCount } from '../hooks';
import type { NotificationApp } from '../types';

interface NotificationBellProps {
  /** App filter for unread count (optional - shows all if not specified) */
  app?: NotificationApp;
  /** Click handler */
  onClick?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Size of the bell icon */
  size?: 'sm' | 'md' | 'lg';
  /** Accessible label */
  'aria-label'?: string;
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

const badgeSizeClasses = {
  sm: 'h-4 min-w-4 text-[10px] -top-1 -right-1',
  md: 'h-5 min-w-5 text-xs -top-1.5 -right-1.5',
  lg: 'h-6 min-w-6 text-sm -top-2 -right-2',
};

const dotSizeClasses = {
  sm: 'h-2 w-2 top-0.5 right-0.5',
  md: 'h-2.5 w-2.5 top-0.5 right-0.5',
  lg: 'h-3 w-3 top-1 right-1',
};

/**
 * Simple bell icon fallback when data fetching fails
 */
function NotificationBellFallback({
  onClick,
  className,
  size = 'md',
  ariaLabel = 'Notifications',
}: {
  onClick?: () => void;
  className?: string;
  size: 'sm' | 'md' | 'lg';
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative inline-flex items-center justify-center',
        'rounded-md p-2',
        'text-muted-foreground hover:text-foreground',
        'hover:bg-muted/50',
        'transition-colors duration-200',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className
      )}
      aria-label={ariaLabel}
    >
      <RiNotification3Line className={sizeClasses[size]} />
    </button>
  );
}

/**
 * Internal component that uses the notification hook
 */
function NotificationBellContent({
  app,
  onClick,
  className,
  size = 'md',
  ariaLabel = 'Notifications',
}: {
  app?: NotificationApp;
  onClick?: () => void;
  className?: string;
  size: 'sm' | 'md' | 'lg';
  ariaLabel: string;
}) {
  // Query for unread count without throwing to avoid crashing the page.
  // If the request fails before any data is loaded, render an "unavailable" indicator
  // rather than a fake 0. A failed refetch preserves the last known unreadCount.
  const { data, isError } = useUnreadNotificationCount(app, {
    enabled: true,
  });
  const unreadCount = data ?? 0;
  const countUnavailable = isError && data === undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative inline-flex items-center justify-center',
        'rounded-md p-2',
        'text-muted-foreground hover:text-foreground',
        'hover:bg-muted/50',
        'transition-colors duration-200',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className
      )}
      aria-label={ariaLabel}
    >
      <RiNotification3Line className={sizeClasses[size]} />
      {countUnavailable ? (
        <>
          <span
            className={cn('absolute rounded-full bg-muted-foreground', dotSizeClasses[size])}
            title="Notification count unavailable"
            aria-hidden="true"
          />
          <span className="sr-only">Notification count unavailable</span>
        </>
      ) : (
        unreadCount > 0 && (
          <>
            <span
              className={cn(
                'absolute flex items-center justify-center',
                'rounded-full bg-destructive text-destructive-foreground',
                'font-medium px-1',
                badgeSizeClasses[size]
              )}
              aria-hidden="true"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
            <span className="sr-only">{unreadCount} unread notifications</span>
          </>
        )
      )}
    </button>
  );
}

/**
 * Error boundary specifically for NotificationBell
 * Catches any errors and renders a fallback bell icon
 */
class NotificationBellErrorBoundary extends Component<
  {
    children: ReactNode;
    fallback: ReactNode;
  },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.warn('[NotificationBell] Error caught by boundary:', error.message, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

/**
 * Main NotificationBell component with built-in error handling
 */
export function NotificationBell({
  app,
  onClick,
  className,
  size = 'md',
  'aria-label': ariaLabel = 'Notifications',
}: NotificationBellProps) {
  const fallback = (
    <NotificationBellFallback
      onClick={onClick}
      className={className}
      size={size}
      ariaLabel={ariaLabel}
    />
  );

  return (
    <NotificationBellErrorBoundary fallback={fallback}>
      <NotificationBellContent
        app={app}
        onClick={onClick}
        className={className}
        size={size}
        ariaLabel={ariaLabel}
      />
    </NotificationBellErrorBoundary>
  );
}
